import { useEffect, useState } from 'react';
import { History, Upload } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { KbVersion } from '@/types';

export function Versions() {
  const [versions, setVersions] = useState<KbVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [notes, setNotes] = useState('');

  async function load() {
    setError(null);
    try {
      const v = await api.listVersions();
      setVersions(v);
    } catch (e) {
      const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
      setError(msg);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function publish() {
    if (!window.confirm('Publish a new KB snapshot from all currently-published workflows?')) return;
    setPublishing(true);
    setError(null);
    try {
      const result = await api.publishKb(notes.trim() || undefined);
      setNotes('');
      await load();
      alert(`Published v${result.version} with ${result.workflow_count} workflow(s).`);
    } catch (e) {
      const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
      setError(msg);
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Versions</h2>
          <p className="text-gray-500 mt-1">Published KB snapshots</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-1">Publish a new version</h3>
        <p className="text-sm text-gray-500 mb-4">
          Snapshots all workflows currently in{' '}
          <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">published</span> status into an
          immutable JSON file the agent can serve from.
        </p>
        <div className="flex gap-3">
          <input
            type="text"
            className="input flex-1"
            placeholder="Optional notes (e.g. 'Added prescription workflow')"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <button onClick={publish} disabled={publishing} className="btn-primary">
            <Upload size={16} className="mr-2" />
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {versions === null && <div className="p-6 text-sm text-gray-500">Loading…</div>}

        {versions && versions.length === 0 && (
          <div className="p-12 text-center">
            <History size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">No versions yet</h3>
            <p className="text-sm text-gray-500">
              Publish your first version when you're ready for the agent to start using your workflows.
            </p>
          </div>
        )}

        {versions && versions.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Version
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Published
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Workflows
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Notes
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  JSON
                </th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-b border-gray-100">
                  <td className="py-3 px-4 font-mono font-semibold">v{v.version_number}</td>
                  <td className="py-3 px-4 text-gray-600 text-xs">
                    {new Date(v.published_at).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">{v.workflow_count}</td>
                  <td className="py-3 px-4 text-gray-700">
                    {v.notes || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3 px-4 text-xs">
                    <span className="font-mono break-all text-gray-500" title={v.json_url}>
                      {v.json_url}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
